import { Type, type Static } from "@fastify/type-provider-typebox";
import { SettingsResponse } from "./setting";
import { SyncJobStatus } from "./sync";

export enum UserRole {
  ADMIN = "ADMIN",
  SELLER = "SELLER",
  SUPPORT = "SUPPORT",
  ASSISTANT = "ASSISTANT",
}

export enum UserStatus {
  /**
   * An Incomplete user has signed up with an email and password, but has not filled out their payment info.
   * This is a special case where the user cannot do literally anything except for enter payment information,
   * and their password can be overridden if the user tries to sign up again with the same email.
   *
   * This allows users who have signed up with an email/password but left before paying to come back and
   * either sign up again or log in.
   */
  INCOMPLETE = "INCOMPLETE",

  // User has paid for the subscription, and needs to go through email verification
  EMAIL_VERIFICATION = "EMAIL_VERIFICATION",
  
  NORMAL = "NORMAL",
}

// Tracking recurly account states
// https://recurly.com/developers/api/v2021-02-25/#operation/list_accounts
export enum PaymentStatus {
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CLOSED = "closed",
  SUBSCRIBER = "subscriber",
  NON_SUBSCRIBER = "non_subscriber"
}

//
// Sign Up
//
export const AuthSignupBody = Type.Object({
  name: Type.String(),
  email: Type.String(),
  confirmEmail: Type.String(),
  password: Type.String(),
  captcha: Type.String(),
  xid: Type.Optional(Type.String()), // 'click id', for partnerstack, only provided if signing-up through affiliate
});
export type AuthSignupBody = Static<typeof AuthSignupBody>;

export const AuthSignupResponse = Type.Object({
  accessToken: Type.String(),
  user: Type.Object({
    uuid: Type.String(),
    email: Type.String(),
    name: Type.String(),
    status: Type.Enum(UserStatus),
    role: Type.Enum(UserRole),
    sellerId: Type.String(),
    addresses: Type.Array(Type.Object({
      id: Type.Number(),
      name: Type.String(),
      company: Type.Optional(Type.String()),
      address1: Type.String(),
      address2: Type.Optional(Type.String()),
      city: Type.String(),
      state: Type.String(),
      country: Type.Optional(Type.String()),
      zipcode: Type.String(),
      phone: Type.Optional(Type.String()),
      email: Type.Optional(Type.String()),
    })),
  }),
});
export type AuthSignupResponse = Static<typeof AuthSignupResponse>;

export const AuthVerifyOTPBody = Type.Object({
  otp: Type.String({ minLength: 6, maxLength: 6 }),
});
export type AuthVerifyOTPBody = Static<typeof AuthVerifyOTPBody>;

//
// Log In
//
export const AuthLoginBody = Type.Object({
  email: Type.String(),
  password: Type.String(),
});
export type AuthLoginBody = Static<typeof AuthLoginBody>;

export const Address = Type.Object({
  id: Type.Number(),
  name: Type.String(),
  company: Type.Optional(Type.String()),
  address1: Type.String(),
  address2: Type.Optional(Type.String()),
  city: Type.String(),
  state: Type.String(),
  country: Type.Optional(Type.String()),
  zipcode: Type.String(),
  phone: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
});
export type Address = Static<typeof Address>;

export const ShippingTemplate = Type.Object({
  id: Type.Number(),
  name: Type.String(),
});
export type ShippingTemplate = Static<typeof ShippingTemplate>;

export const UserResponse = Type.Object({
  uuid: Type.String(),
  email: Type.String(),
  emailVerified: Type.Optional(Type.Boolean()),
  name: Type.String(),
  status: Type.Enum(UserStatus),
  role: Type.Enum(UserRole),
  sellerId: Type.String(),
  activeMarketplaceId: Type.Optional(Type.String()),
  forceChangePassword: Type.Boolean(),
  addresses: Type.Optional(Type.Array(Address)),
  recurlyAccountCode: Type.String(),
  paymentStatus: Type.Enum(PaymentStatus),
  isSubscribed: Type.Boolean(),
  settings: Type.Optional(SettingsResponse),
  shippingTemplates: Type.Optional(Type.Array(ShippingTemplate)),
  listingSyncStatus: Type.Union([Type.Enum(SyncJobStatus), Type.Null()]),
  shipmentSyncStatus: Type.Union([Type.Enum(SyncJobStatus), Type.Null()]),
  //shipmentItemSyncStatus: Type.Union([Type.Enum(SyncJobStatus), Type.Null()]),
  //shipmentTransportSyncStatus: Type.Union([Type.Enum(SyncJobStatus), Type.Null()]),
  allSalesSyncStatus: Type.Union([Type.Enum(SyncJobStatus), Type.Null()]),
  sellers: Type.Optional(Type.Array(Type.Object({
    permissions: Type.Integer(),
    uuid: Type.String(),
    email: Type.String(),
    name: Type.String(),
    role: Type.Enum(UserRole),
    sellerId: Type.String(),
    recurlyAccountCode: Type.String(),
    paymentStatus: Type.Enum(PaymentStatus),
    isSubscribed: Type.Boolean(),
    emailVerified: Type.Boolean(),
    settings: Type.Optional(SettingsResponse),
    addresses: Type.Optional(Type.Array(Address)),
    shippingTemplates: Type.Optional(Type.Array(ShippingTemplate))
  }))),
  goAuraIntegration: Type.Boolean(),
  sellerSnapIntegration: Type.Boolean(),
  bqoolIntegration: Type.Boolean()
});
export type UserResponse = Static<typeof UserResponse>;

export const AuthLoginResponse = Type.Object({
  accessToken: Type.String(),
  user: UserResponse
});
export type AuthLoginResponse = Static<typeof AuthLoginResponse>;

//
// Verify Email
//
export const AuthVerifyEmailBody = Type.Object({
  token: Type.String(),
});
export type AuthVerifyEmailBody = Static<typeof AuthVerifyEmailBody>;

//
// Connect to Seller Central
//
export const AuthConnectSellerCentralBody = Type.Object({
  code: Type.String(),
  sellerId: Type.String(),
  redirectUri: Type.String(),
});
export type AuthConnectSellerCentralBody = Static<typeof AuthConnectSellerCentralBody>;

export const AuthForgotPasswordBody = Type.Object({
  email: Type.String(),
});
export type AuthForgotPasswordBody = Static<typeof AuthForgotPasswordBody>;

export const AuthResetPasswordBody = Type.Object({
  password: Type.String(),
  token: Type.String(),
});
export type AuthResetPasswordBody = Static<typeof AuthResetPasswordBody>;

export const AuthChangePasswordBody = Type.Object({
  currentPassword: Type.String({ minLength: 8, maxLength: 64 }),
  newPassword: Type.String({ minLength: 8, maxLength: 64 }),
});
export type AuthChangePasswordBody = Static<typeof AuthChangePasswordBody>;

export const AuthChangeEmailBody = Type.Object({
  email: Type.String({ maxLength: 255 })
});
export type AuthChangeEmailBody = Static<typeof AuthChangeEmailBody>;
